import { addDays, addHours, addMonths, addWeeks, format, startOfHour, startOfMonth, startOfWeek } from "date-fns";
import { DateFormat } from "common/helpers/Format";

export type UrlView = "day" | "week" | "work_week" | "month";
export type FcViewType = "timeGridDay" | "timeGridWeek" | "timeGridWeekFiveDay" | "dayGridMonth";

const URL_VIEWS: readonly UrlView[] = ["day", "week", "work_week", "month"];
const DEFAULT_URL_VIEW: UrlView = "month";

export const URL_VIEW_TO_FC: Record<UrlView, FcViewType> = {
  day: "timeGridDay",
  week: "timeGridWeek",
  work_week: "timeGridWeekFiveDay",
  month: "dayGridMonth",
};

export const FC_VIEW_TO_URL: Record<FcViewType, UrlView> = {
  timeGridDay: "day",
  timeGridWeek: "week",
  timeGridWeekFiveDay: "work_week",
  dayGridMonth: "month",
};

export const isUrlView = (s: string | null | undefined): s is UrlView =>
  s != null && (URL_VIEWS as readonly string[]).includes(s);

export const toUrlView = (fcViewType: string): UrlView =>
  (FC_VIEW_TO_URL as Record<string, UrlView | undefined>)[fcViewType] ?? DEFAULT_URL_VIEW;

export const toFcView = (urlView: UrlView): FcViewType => URL_VIEW_TO_FC[urlView];

const DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

// `new Date("YYYY-MM-DD")` parses as UTC midnight and shifts the day in non-UTC
// zones; appending "T00:00:00" forces local-TZ midnight so the URL `date`
// represents the user-facing calendar day in their own TZ. Also rejects JS
// date rollover (e.g. 2026-02-30 → Mar 2) by checking the parsed components
// against the raw input — getTime() does NOT flag rollover.
export const parseUrlDate = (raw: string | null | undefined): Date | null => {
  if (!raw) return null;
  const match = DATE_PATTERN.exec(raw);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const parsed = new Date(raw + "T00:00:00");
  if (Number.isNaN(parsed.getTime())) return null;
  if (parsed.getFullYear() !== year || parsed.getMonth() + 1 !== month || parsed.getDate() !== day) return null;
  return parsed;
};

// Delegates to the shared DateFormat so the `yyyy-MM-dd` format string is
// defined in one place (Format.ts under `"shortDate-hyphenated"`). All callers
// in this module pass a valid Date built by parseUrlViewDate, so DateFormat's
// "Invalid date" fallback string will never surface in a URL.
export const formatUrlDate = (date: Date): string => DateFormat(date, "shortDate-hyphenated");

export interface ParsedUrlViewDate {
  urlView: UrlView;
  fcViewType: FcViewType;
  date: Date;
  /** True when at least one of the raw URL inputs was missing or invalid. */
  normalized: boolean;
}

export const parseUrlViewDate = (searchParams: URLSearchParams): ParsedUrlViewDate => {
  const rawView = searchParams.get("view");
  const rawDate = searchParams.get("date");
  const viewValid = isUrlView(rawView);
  const parsedDate = parseUrlDate(rawDate);
  const urlView: UrlView = viewValid ? (rawView as UrlView) : DEFAULT_URL_VIEW;
  const referenceDate = parsedDate ?? new Date();
  // Pin to local midnight so downstream comparisons (and the URL value) are
  // stable regardless of whether the source was a clock-bearing `new Date()`
  // or an already-midnight parse from the URL.
  const date = new Date(referenceDate.getFullYear(), referenceDate.getMonth(), referenceDate.getDate());
  return {
    urlView,
    fcViewType: URL_VIEW_TO_FC[urlView],
    date,
    normalized: !viewValid || parsedDate === null,
  };
};

// FullCalendar's args.view.currentStart for each view type, derived from a
// reference local-midnight date. The component uses this to seed
// lastWrittenViewDateRef so the ref matches what FC will fire on its first
// datesSet (preventing a spurious URL rewrite from user-typed date to
// view-aligned date). weekStartsOn matches FC's default (Sunday).
export const viewStartDate = (urlView: UrlView, date: Date): Date => {
  const fcViewType = URL_VIEW_TO_FC[urlView];
  if (fcViewType === "dayGridMonth") return startOfMonth(date);
  if (fcViewType === "timeGridWeek" || fcViewType === "timeGridWeekFiveDay") {
    return startOfWeek(date, { weekStartsOn: 0 });
  }
  return date;
};

// Used by the loader to compute the fetch range without a live FullCalendar instance.
const visibleRange = (fcViewType: FcViewType, date: Date): { start: Date; end: Date } => {
  if (fcViewType === "dayGridMonth") {
    const start = startOfMonth(date);
    return { start, end: addMonths(start, 1) };
  }
  if (fcViewType === "timeGridWeek" || fcViewType === "timeGridWeekFiveDay") {
    const start = startOfWeek(date, { weekStartsOn: 0 });
    return { start, end: addDays(start, 7) };
  }
  return { start: date, end: addDays(date, 1) };
};

// View → ±padding to apply when computing the API fetch range. Matches the
// spec's "Date range fetched (matches rsf-app getPeriod)" table:
//   Month             → ±1 week
//   Week / Work Week  → ±1 day
//   Day               → ±1 day (3-day window)
//
// Takes FullCalendar view type strings — translation from URL view happens at
// the URL boundary, not inside padRange. Keeps the helper coherent with
// FullCalendar's args.view.type input shape.
export const padRange = (fcViewType: string, start: Date, end: Date): { start: Date; end: Date } => {
  if (fcViewType === "dayGridMonth") {
    return { start: addWeeks(start, -1), end: addWeeks(end, 1) };
  }
  return { start: addDays(start, -1), end: addDays(end, 1) };
};

export const computeFetchRange = (urlView: UrlView, date: Date): { start: Date; end: Date } => {
  const fcViewType = URL_VIEW_TO_FC[urlView];
  const visible = visibleRange(fcViewType, date);
  return padRange(fcViewType, visible.start, visible.end);
};

// Anchors FullCalendar's `scrollTime` at the next on-the-hour slot (14:32 → "15:00:00").
export const nextHourScrollTime = (): string => {
  const now = new Date();
  const next = addHours(startOfHour(now), 1);
  return format(next, "HH:mm:ss");
};
